import { Injectable } from '@angular/core';
import { PolicyCheckerService } from './policyChecker.service';
import { getFeaturesWithHierarchyByRole } from './features';

/**
 * @fileOverview Access control and role-based navigation management service
 * @description Manages role-based access control (RBAC) and dynamic navigation structure generation.
 * Integrates with policy checking to determine which features are accessible based on user role
 * and active subscriptions. Builds hierarchical navigation menus reflecting user permissions.
 * 
 * @module roleAccessController
 */

/**
 * @service AccessControllerService
 * @description Service for managing access control and building role-based navigation structures.
 * Orchestrates role resolution, feature filtering, and navigation hierarchy construction.
 * Works with PolicyCheckerService to determine accessible features based on subscriptions.
 * 
 * Core Responsibilities:
 * - Verify that navigation structure can be built for the current user
 * - Determine user role and business category from localStorage
 * - Filter available features based on subscriptions and policies
 * - Normalize feature definitions for UI consumption
 * - Build hierarchical navigation menus (main menu → sub menu → features)
 * - Persist navigation structure to localStorage for app-wide access
 * 
 * Role-Based Features:
 * - Dynamic role labeling (Business Admin → Provider/Farm Business Admin)
 * - Business category-aware feature filtering
 * - Multi-level hierarchical navigation support (level 0, 1, 2)
 * - Icon and route parameter management
 * - Subscription-aware feature availability
 * 
 * Architecture:
 * - Role determined from 'loginPersonType' in localStorage
 * - Features fetched from role-based feature definitions
 * - Accessibility filtered by PolicyCheckerService
 * - Navigation persisted in localStorage under 'Nav_Structure'
 * 
 * @example
 * // In a component
 * @Component({...})
 * export class DashboardComponent {
 *   constructor(private acl: AccessControllerService) {}
 * 
 *   ngOnInit() {
 *     if (this.acl.isBuildArchitectResolved()) {
 *       const navStructure = this.acl.getNavigationStructure();
 *       this.buildMenu(navStructure);
 *     }
 *   }
 * }
 */
@Injectable({
  providedIn: 'root',
})
export class AccessControllerService {
  constructor(private policyCheckerService: PolicyCheckerService) {}

  /**
   * @method isBuildArchitectResolved
   * @description Checks whether the navigation structure (Build Architect) can be successfully built
   * for the currently logged-in user. Acts as a guard to determine if the app's navigation is ready.
   * 
   * Process:
   * 1. Initializes navigation structure based on user role and subscriptions
   * 2. Returns true if at least one feature is accessible and menu hierarchy is built
   * 3. Returns false if no role is found or no accessible features exist
   * 
   * Usage: Typically checked in app initialization to determine if user has any access.
   * 
   * @returns {boolean} True if navigation structure is built with at least one menu item,
   * false if user has no role or no accessible features
   * 
   * @example
   * if (this.acl.isBuildArchitectResolved()) {
   *   // User has access, proceed with app initialization
   *   const nav = this.acl.getNavigationStructure();
   * } else {
   *   // No accessible features, show access denied or redirect
   *   this.router.navigate(['/unauthorized']);
   * }
   */
  public isBuildArchitectResolved(): boolean {
    const navBuilt = this.initializeNavStructure();
    return navBuilt > 0;
  }

  /**
   * @method initializeNavStructure
   * @description Initializes the navigation structure based on the logged-in user's role
   * and their accessible features (determined by active subscriptions and policies).
   * This is the main orchestrator that:
   * 1. Retrieves user role from localStorage ('loginPersonType')
   * 2. Transforms role based on business category (Farm/Provider Business Admin)
   * 3. Fetches all possible features for the role
   * 4. Filters features based on policy/subscription access
   * 5. Normalizes features and builds hierarchical menu
   * 6. Persists the navigation structure to localStorage
   * 
   * Role Transformation:
   * - 'Business Admin' + business category → '{Category} Business Admin'
   *   (e.g., 'Farm Business Admin', 'Provider Business Admin')
   * - Other roles remain unchanged
   * 
   * @private
   * @returns {number} Count of top-level navigation items built.
   * Returns 0 if no role found or no accessible features exist.
   * 
   * @example
   * // This is called internally by isBuildArchitectResolved()
   * const menuCount = this.initializeNavStructure();
   * // menuCount > 0 means navigation was successfully built
   */
  private initializeNavStructure(): number {
    // Step 1: Get the logged-in role
    const role = localStorage.getItem('loginPersonType');
    if (!role) {
      console.warn('No role found in localStorage.');
      return 0; 
    }

    // Get logged-in user details from localStorage
    const businessCategory = localStorage.getItem('loginBusinessCategory') || '';

    /**
     * @description Role Label Transformation Logic
     * Updated role label based on user's business category:
     * - If user is a Business Admin:
     *    → Show "Provider Business Admin" when businessCategory = Provider
     *    → Show "Farm Business Admin" when businessCategory = Farm
     * - Otherwise: keep the original role unchanged
     * 
     * This enables role-specific feature sets per business type (e.g., Farm Business Admin
     * has different accessible features than Provider Business Admin).
     */
    const updatedRole =
      role === 'Business Admin'
        ? `${businessCategory} Business Admin`
        : role;

    // Step 2: Fetch all possible feature objects for this role
    const roleFeatures = getFeaturesWithHierarchyByRole(updatedRole);

    // Step 3: Get accessible feature IDs from policy checker (based on subscriptions)
    const accessibleFeatureIds: string[] =
      this.policyCheckerService.getUserAccessibleFeatures();

    // Step 4: Filter and normalize features allowed for this role
    const allowedFeatures = roleFeatures
      .filter((feature) => accessibleFeatureIds.includes(feature.id))
      .map((feature) => this.normalizeFeature(feature));

    // Step 5: Build navigation structure and persist in localStorage
    return this.buildNavigationStructure(allowedFeatures);
  }

  /**
   * @method normalizeFeature
   * @description Normalizes a raw feature object from the feature definitions into a clean,
   * UI-friendly structure. Removes unnecessary fields and extracts only what's needed
   * for navigation/menu rendering and routing.
   * 
   * Extracted Fields:
   * - id: Unique feature identifier
   * - name: Display name for menu/feature
   * - hierarchy: Menu hierarchy (mainMenu, subMenu, level, order)
   * - route: Navigation routing information (path, params, queryParams)
   * - icons: Icon definitions (menu, group, main icons)
   * 
   * @private
   * @param {any} feature - Raw feature object from feature definitions
   * @returns {{
   *   id: string,
   *   name: string,
   *   hierarchy: any,
   *   route: any,
   *   icons: any
   * }} Normalized feature object with only UI-relevant fields
   * 
   * @example
   * const rawFeature = {
   *   id: 'dashboard',
   *   name: 'Dashboard',
   *   ui: { hierarchy: {...}, route: {...}, icons: {...} },
   *   metadata: {...}, // Removed
   *   backendConfig: {...} // Removed
   * };
   * const normalized = this.normalizeFeature(rawFeature);
   * // Result: { id, name, hierarchy, route, icons }
   */
  private normalizeFeature(feature: any) {
    return {
      id: feature.id,
      name: feature.name,
      hierarchy: feature.ui?.hierarchy || null,
      route: feature.ui?.route || null,
      icons: feature.ui?.icons || null,
    };
  }

  /**
   * @method buildNavigationStructure
   * @description Builds a hierarchical navigation structure (tree) based on provided features.
   * Processes features by their hierarchy level (0, 1, 2) to create menus and sub-menus.
   * Persists the complete navigation structure to localStorage under 'Nav_Structure' key
   * for app-wide access via getNavigationStructure().
   * 
   * Hierarchy Levels:
   * - Level 0: Top-level features (standalone items in navigation)
   * - Level 1: Features under a Main Menu (first layer of grouping)
   * - Level 2: Features under a Sub Menu (second layer of grouping)
   * 
   * Menu Structure:
   * ```
   * [{
   *   type: 'menu',
   *   name: 'Main Menu Name',
   *   icon: 'menu-icon',
   *   order: 1,
   *   children: [
   *     {
   *       type: 'submenu',
   *       name: 'Sub Menu Name',
   *       icon: 'group-icon',
   *       order: 1,
   *       children: [
   *         {
   *           type: 'feature',
   *           name: 'Feature Name',
   *           routePath: '/path',
   *           icon: 'feature-icon',
   *           order: 1
   *         }
   *       ]
   *     },
   *     {
   *       type: 'feature',
   *       name: 'Direct Feature',
   *       routePath: '/direct',
   *       order: 2
   *     }
   *   ]
   * }]
   * ```
   * 
   * @private
   * @param {any[]} features - Array of normalized features to structure
   * @returns {number} Count of top-level items in the navigation structure
   * (useful for determining if any menu was built)
   * 
   * @example
   * const features = [
   *   {
   *     id: 'dashboard',
   *     name: 'Dashboard',
   *     hierarchy: { mainMenu: 'Home', level: 1, order: 1 },
   *     route: { path: '/dashboard' },
   *     icons: { menu: 'home-icon' }
   *   }
   * ];
   * const count = this.buildNavigationStructure(features);
   * const nav = JSON.parse(localStorage.getItem('Nav_Structure'));
   */
  private buildNavigationStructure(features: any[]): number {
    const navStructure: any[] = [];
    const accessibleFeatures = features || [];

    accessibleFeatures.forEach((feature) => {
      const { hierarchy, icons, route, name } = feature;
      const { mainMenu, subMenu, level, order } = hierarchy || {};

      // ---- Helper to find or create a Main Menu ----
      const getOrCreateMainMenu = () => {
        let menu = navStructure.find(
          (m) => m.type === 'menu' && m.name === mainMenu
        );
        if (!menu) {
          menu = {
            type: 'menu',
            name: mainMenu,
            children: [],
            icon: icons?.menu || '',
            order,
          };
          navStructure.push(menu);
        } else if (!menu.icon && (icons?.menu)) {
          menu.icon = icons?.menu;
          menu.order = order;
        }
        return menu;
      };

      // ---- Helper to find or create a Sub Menu ----
      const getOrCreateSubMenu = (menu: any) => {
        let submenu = menu.children.find(
          (s) => s.type === 'submenu' && s.name === subMenu
        );
        if (!submenu) {
          submenu = {
            type: 'submenu',
            name: subMenu,
            children: [],
            icon: icons?.group || '',
            order,
          };
          menu.children.push(submenu);
        } else if (!submenu.icon && icons?.group) {
          submenu.icon = icons?.group;
          submenu.order = order;
        }
        return submenu;
      };

      // ---- Feature Data ----
      const featureData = {
        type: 'feature',
        name,
        routePath: route?.path,
        params: route?.params,
        queryParams: route?.queryParams,
        icon: icons?.main,
        order,
      };

      // ---- Add Feature Based on Hierarchy Level ----
      if (level === 0) {
        // Top-level direct feature
        navStructure.push(featureData);
      } else if (level === 1) {
        // Feature under a Main Menu
        const main = getOrCreateMainMenu();
        main.children.push({ ...featureData, icon: icons?.main });
      } else if (level === 2) {
        // Feature under a Sub Menu
        const main = getOrCreateMainMenu();
        const sub = getOrCreateSubMenu(main);
        sub.children.push(featureData);
      }
    });

    // Persist nav structure in localStorage
    localStorage.setItem('Nav_Structure', JSON.stringify(navStructure));
    
    return navStructure.length;
  }

  /**
   * @method getNavigationStructure
   * @description Retrieves the complete hierarchical navigation structure from browser localStorage.
   * This structure is built and persisted by buildNavigationStructure() during access control
   * initialization. The navigation structure represents all menu items, submenus, and features
   * accessible to the current user based on their role and active subscription policies.
   * 
   * The returned structure is typically consumed by:
   * - Sidebar/menu components for rendering the main navigation
   * - Route guards for dynamically determining accessible paths
   * - Breadcrumb components for showing navigation context
   * - Permission checks before routing or displaying UI elements
   * 
   * Storage Location: localStorage['Nav_Structure']
   * 
   * @public
   * @returns {any} The hierarchical navigation structure object, or null if not yet built.
   * Structure format:
   * ```typescript
   * [
   *   {
   *     type: 'menu',
   *     name: string,
   *     icon?: string,
   *     order: number,
   *     children: [
   *       {
   *         type: 'submenu' | 'feature',
   *         name: string,
   *         routePath?: string,
   *         icon?: string,
   *         order: number,
   *         children?: Array<...> // For submenus
   *       }
   *     ]
   *   }
   * ]
   * ```
   * 
   * @example
   * // Get the navigation structure
   * const navStructure = this.roleAccessController.getNavigationStructure();
   * 
   * // Render in sidebar component
   * @Component({
   *   selector: 'app-sidebar',
   *   template: `
   *     <nav *ngFor="let item of navStructure">
   *       <button [routerLink]="item.routePath">{{ item.name }}</button>
   *       <ul *ngIf="item.children">
   *         <li *ngFor="let child of item.children">
   *           {{ child.name }}
   *         </li>
   *       </ul>
   *     </nav>
   *   `
   * })
   * export class SidebarComponent {
   *   navStructure = this.roleAccessController.getNavigationStructure();
   *   constructor(private roleAccessController: RoleAccessControllerService) {}
   * }
   * 
   * // Check if feature is accessible
   * const navStructure = this.roleAccessController.getNavigationStructure();
   * const isAccessible = this.findFeatureInNav(navStructure, 'dashboard');
   */
  public getNavigationStructure(): any {
    return JSON.parse(localStorage.getItem('Nav_Structure') ?? 'null');
  }
}
